/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getLastSelectedCompany,
  getLinkedCompanies,
  getProfile,
  logoutUser,
  updateLastSelectedCompany,
  updateProfile,
} from '../api/auth';
import { getCompanyFeatures } from '@/api/company';
import type { CompanyFeatureFlags } from '@gokrew/models/company';
import {
  UserRole,
  type AuthTokensResponse,
  type User,
  type ProfileResponse,
  type SignupResponse,
} from '@/types/auth';
import { stopMockLogin } from '@/api/mock-login';
import type { Company } from '@/types/company';
import { UtmStorageKey } from '@gokrew/models/constants';
import { QueryKeys } from '@/constants/query-keys';
import { SessionExpiredDialog } from '@/utility/SessionExpiredDialog';
import { LAST_COMPANY_KEY, useDialog, useUserback } from '@gokrew/ui';
import { decodeAccessTokenClaims } from '@gokrew/ui/auth';
import { consumeIntentionalLanguage } from '@gokrew/ui/utility/intentional-language';
import { tokenManager } from '@/auth';
import { sessionPrewarm } from '@/auth/sessionPrewarm';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import i18n from '@/i18n';
import { companyDisplayName } from '@gokrew/ui/utility/companyDisplayName';

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean; // overall auth loading
  isInitializing: boolean; // true while silent session restore is in progress
  isError: boolean;
  branchId: string | null;
  companyId?: string | null;
  hasCompany: boolean | null;
  companyName: string | null;
  companies?: Company[] | null;
  role: UserRole | null;
  setHasCompany: (value: boolean) => void;
  setCompanyName: (value: string) => void;
  setCompanyId: (value: string | null) => void;
  setAuth: (data: AuthTokensResponse | SignupResponse) => void;
  setMockLoginToken: (accessToken: string, companyId?: string | null) => void;
  endMockSession: () => Promise<void>;
  isMockLogin: boolean;
  mockLoginSessionId: string | null;
  mockLoginExpiresAt: number | null;
  changeCompany: (companyId: Company) => void;
  logout: () => Promise<void>;
  refetchCompanies: () => void;
  handleSetBranch: (val: string | null) => void;
  selectedCompany?: Company | null;
  refetchProfile: () => void;
  featureFlags: CompanyFeatureFlags | null;
  /** Whether timesheets actually run, as opposed to merely being shown. */
  timesheetActive: boolean | null;
  isFeatureFlagsLoading: boolean;
}

const VALID_ROLES = new Set<string>(Object.values(UserRole));

/** Per-tab company a super admin mock login ("Open as HR") is pinned to. */
const MOCK_COMPANY_KEY = 'frontend_mock_company_id';
const clearMockStorage = () =>
  ['frontend_mock_token', 'frontend_mock_session_id', MOCK_COMPANY_KEY].forEach(
    (key) => sessionStorage.removeItem(key)
  );

function parseStoredRole(): UserRole | null {
  const stored = localStorage.getItem('userRole');
  return stored && VALID_ROLES.has(stored) ? (stored as UserRole) : null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { openDialog } = useDialog();
  const { t } = useTranslation('common');
  const sessionExpiredHandled = useRef(false);

  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [role, setRole] = useState<UserRole | null>(parseStoredRole);
  const [isMockLogin, setIsMockLogin] = useState(false);
  const [mockLoginSessionId, setMockLoginSessionId] = useState<string | null>(
    sessionStorage.getItem('frontend_mock_session_id')
  );
  const [mockLoginExpiresAt, setMockLoginExpiresAt] = useState<number | null>(
    null
  );
  const [companyId, setCompanyId] = useState<string | null>(
    localStorage.getItem('companyId')
  );
  const [companyName, setCompanyName] = useState<string | null>(
    localStorage.getItem('companyName')
  );
  const [branchId, setBranchId] = useState<string | null>(
    localStorage.getItem('branchId')
  );
  // A link that names its company (e.g. a change-request email) opens in that
  // company. Read once on page load, so a later refetch of the company list
  // cannot undo a switch the admin makes afterwards.
  const linkCompanyId = useRef(
    new URLSearchParams(window.location.search).get('companyId')
  );

  const handleSetBranch = (val: string | null) => {
    setBranchId(val);
    if (val) {
      localStorage.setItem('branchId', val);
    } else {
      localStorage.removeItem('branchId');
    }
  };

  // const terminateSessionMutation = useMutation({
  //   mutationFn: (sessionId: string) => terminateSession(sessionId),
  //   onSuccess: () => console.log('Session terminated successfully'),
  //   onError: (err: unknown) =>
  //     console.error('Failed to terminate session', err),
  // });

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: () => console.log('Logged out successfully'),
  });

  const softLogout = () => {
    tokenManager.clearToken();
    setToken(null);
    setCompanyId(null);
    setCompanyName(null);
    setHasCompany(false);
    setRole(null);
    setIsMockLogin(false);
    setMockLoginSessionId(null);
    setMockLoginExpiresAt(null);
    localStorage.removeItem('branchId');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
    localStorage.removeItem(LAST_COMPANY_KEY);
    // Marketing attribution is read server-side from the UTM cookie; the
    // localStorage mirror is tenant-scoped residue, so drop it on logout.
    localStorage.removeItem(UtmStorageKey.UTM_PARAMS);
    clearMockStorage();
    queryClient.clear();
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      console.error('Logout API failed', err);
    } finally {
      softLogout();
      toast.success(t('auth.logout_success'));
    }
  };

  const handleSessionExpired = () => {
    if (sessionExpiredHandled.current) return;
    sessionExpiredHandled.current = true;

    const wasMockLogin = !!sessionStorage.getItem('frontend_mock_token');
    if (wasMockLogin) {
      tokenManager.clearToken();
      setToken(null);
      setIsMockLogin(false);
      setMockLoginSessionId(null);
      setMockLoginExpiresAt(null);
      clearMockStorage();
      queryClient.clear();
      window.location.href = '/mock-session-expired';
    } else {
      softLogout();
      openDialog({
        title: 'Session Expired',
        disableClose: true,
        content: <SessionExpiredDialog />,
      });
    }
  };

  // Register the session-expired callback with the token manager once
  useEffect(() => {
    tokenManager.onSessionExpired(handleSessionExpired);
  }, []);

  // Silently restore session on mount (handles page refresh / new tab)
  useEffect(() => {
    async function restoreSession() {
      try {
        const mockToken = sessionStorage.getItem('frontend_mock_token');
        if (mockToken) {
          tokenManager.setToken(mockToken);
          if (tokenManager.isTokenValid()) {
            const {
              sessionId,
              role: mockRole,
              expiresAt,
            } = decodeAccessTokenClaims(mockToken);
            setToken(mockToken);
            setIsMockLogin(true);
            setMockLoginSessionId(sessionId);
            setMockLoginExpiresAt(expiresAt);
            if (mockRole && VALID_ROLES.has(mockRole))
              setRole(mockRole as UserRole);
          } else {
            clearMockStorage();
            setToken(null);
            window.location.href = '/mock-session-expired';
          }
          return;
        }

        // Only attempt restore if a sessionId exists — it's set on login and
        // cleared on logout, so its absence means there's no session to restore.
        // (We can't read the httpOnly refresh cookie directly to check.)
        if (!localStorage.getItem('sessionId')) {
          setToken(null);
          return;
        }

        // Collects the refresh main.tsx already started, so it is not repeated.
        const restored = await (sessionPrewarm.take() ??
          tokenManager.getValidToken());
        setToken(restored);
      } finally {
        setIsInitializing(false);
      }
    }

    restoreSession();
  }, []);

  const setAuth = (response: AuthTokensResponse | SignupResponse) => {
    const { accessToken } = response.data;
    // sessionId/role are also signed into the access token itself, so
    // decoding it here works regardless of which endpoint issued the token.
    const claims = decodeAccessTokenClaims(accessToken);
    const userRole =
      claims.role && VALID_ROLES.has(claims.role)
        ? (claims.role as UserRole)
        : null;
    tokenManager.setToken(accessToken);
    setToken(accessToken);
    if (claims.sessionId) localStorage.setItem('sessionId', claims.sessionId);
    if (userRole) {
      localStorage.setItem('userRole', userRole);
      setRole(userRole);
    }
    // Clear previous user's company data
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
    localStorage.removeItem(LAST_COMPANY_KEY);
    setCompanyId(null);
    setCompanyName(null);
    setHasCompany(null);
    queryClient.invalidateQueries({ queryKey: [QueryKeys.Profile] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.Companies] });
  };

  const setMockLoginToken = (
    accessToken: string,
    mockCompanyId?: string | null
  ) => {
    const {
      sessionId,
      role: mockRole,
      expiresAt,
    } = decodeAccessTokenClaims(accessToken);
    tokenManager.setToken(accessToken);
    setToken(accessToken);
    setIsMockLogin(true);
    setMockLoginSessionId(sessionId);
    setMockLoginExpiresAt(expiresAt);
    if (mockRole && VALID_ROLES.has(mockRole)) setRole(mockRole as UserRole);
    // Clear stale company data so the permissions query doesn't fire with a
    // wrong companyId before resolveCompany sets the correct one for this user.
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
    localStorage.removeItem(LAST_COMPANY_KEY);
    setCompanyId(null);
    setCompanyName(null);
    setHasCompany(null);
    sessionStorage.setItem('frontend_mock_token', accessToken);
    if (sessionId)
      sessionStorage.setItem('frontend_mock_session_id', sessionId);
    if (mockCompanyId) sessionStorage.setItem(MOCK_COMPANY_KEY, mockCompanyId);
    else sessionStorage.removeItem(MOCK_COMPANY_KEY);
  };

  const endMockSession = async () => {
    if (mockLoginSessionId) {
      try {
        await stopMockLogin(mockLoginSessionId);
      } catch (err) {
        console.error('Failed to stop mock login session', err);
      }
    }
    // Soft clear only — the caller navigates client-side to the expired page, so
    // we avoid a hard reload (which flashes the bootstrap AppLoaderPage). The
    // expired route is public, so tearing down auth state here is safe.
    tokenManager.clearToken();
    setToken(null);
    setCompanyId(null);
    setHasCompany(false);
    setRole(null);
    setIsMockLogin(false);
    setMockLoginSessionId(null);
    setMockLoginExpiresAt(null);
    clearMockStorage();
    queryClient.clear();
  };

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useQuery<ProfileResponse>({
    queryKey: [QueryKeys.Profile],
    queryFn: getProfile,
    enabled: !!token,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
    refetch: refetchCompanies,
  } = useQuery({
    queryKey: [QueryKeys.Companies],
    queryFn: getLinkedCompanies,
    // Independent of the profile: waiting for it only cost a round trip.
    enabled: !!token,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const { data: featureStatus, isLoading: isFeatureFlagsLoading } = useQuery({
    queryKey: [QueryKeys.CompanyFeatures, companyId],
    queryFn: () => getCompanyFeatures(companyId!),
    enabled: !!token && !!companyId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const featureFlags = featureStatus?.featureFlags ?? null;
  const timesheetActive = featureStatus?.timesheetActive ?? null;

  // Update companyId and hasCompany whenever companies change
  useEffect(() => {
    if (!companies) return;

    async function resolveCompany() {
      const list = companies!.data ?? [];
      if (!list.length) {
        setCompanyId(null);
        setCompanyName(null);
        setHasCompany(false);
        localStorage.removeItem('companyId');
        localStorage.removeItem('companyName');
        return;
      }

      // 1. The tab's "Open as HR" company, else the link's company, else
      //    localStorage (no flicker)
      const mockCompanyId = sessionStorage.getItem('frontend_mock_token')
        ? sessionStorage.getItem(MOCK_COMPANY_KEY)
        : null;
      const linkMatch = mockCompanyId
        ? undefined
        : list.find((c) => c.id === linkCompanyId.current);
      linkCompanyId.current = null;
      const storedId = mockCompanyId ?? localStorage.getItem('companyId');
      const localMatch = list.find((c) => c.id === storedId);

      if (linkMatch) {
        // The branch picked belongs to the previous company.
        if (linkMatch.id !== storedId) handleSetBranch(null);
        setCompanyId(linkMatch.id);
        setCompanyName(companyDisplayName(linkMatch));
        setHasCompany(true);
        localStorage.setItem('companyId', linkMatch.id);
        localStorage.setItem('companyName', companyDisplayName(linkMatch));
      } else if (localMatch) {
        setCompanyId(localMatch.id);
        setCompanyName(companyDisplayName(localMatch));
        setHasCompany(true);
        // Still sync from server in background to handle cross-device cases
      } else {
        // 2. Fall back to server-persisted last selected
        try {
          const { lastSelectedCompanyId } = await getLastSelectedCompany();
          const serverMatch = lastSelectedCompanyId
            ? list.find((c) => c.id === lastSelectedCompanyId)
            : null;

          const resolved = serverMatch ?? list[0];
          setCompanyId(resolved.id);
          setCompanyName(companyDisplayName(resolved));
          setHasCompany(true);
          localStorage.setItem('companyId', resolved.id);
          localStorage.setItem('companyName', companyDisplayName(resolved));
        } catch {
          // API failed — just use first company
          const first = list[0];
          setCompanyId(first.id);
          setCompanyName(companyDisplayName(first));
          setHasCompany(true);
          localStorage.setItem('companyId', first.id);
          localStorage.setItem('companyName', companyDisplayName(first));
        }
      }
    }

    resolveCompany();
  }, [companies]);

  const changeCompany = useCallback((company: Company) => {
    const { id } = company;
    const name = companyDisplayName(company);
    setCompanyId(id);
    setCompanyName(name);
    localStorage.setItem('companyId', id);
    localStorage.setItem('companyName', name);
    if (sessionStorage.getItem('frontend_mock_token'))
      sessionStorage.setItem(MOCK_COMPANY_KEY, id);
    updateLastSelectedCompany(id).catch((err) => toast.error(err));
  }, []);

  /**
   * Sync userback user info with the current profile: identify when a
   * profile is available, and clear the previous identity on logout
   * (when profile becomes falsy) so the feedback form doesn't keep
   * showing the last user's name.
   */
  const { userback, setUserInfo, resetUserInfo } = useUserback();
  useEffect(() => {
    if (!profile) {
      resetUserInfo();
      return;
    }
    setUserInfo(profile.data);
  }, [profile, userback, setUserInfo, resetUserInfo]);

  useEffect(() => {
    if (!profile) return;
    const savedLanguage = profile.data.language || 'en';
    // A language picked on the login/signup screen wins and becomes the
    // saved one, instead of being overwritten by the profile on sign-in.
    const intentionalLanguage = consumeIntentionalLanguage();
    if (intentionalLanguage && intentionalLanguage !== savedLanguage) {
      i18n.changeLanguage(intentionalLanguage);
      updateProfile({ language: intentionalLanguage })
        .then(() =>
          queryClient.invalidateQueries({ queryKey: [QueryKeys.Profile] })
        )
        .catch(() => {});
      return;
    }
    if (savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [profile, queryClient]);

  // Overall loading flag
  const isLoading = isProfileLoading || isCompaniesLoading;

  return (
    <AuthContext.Provider
      value={{
        user: profile?.data ?? null,
        token,
        isLoading,
        isInitializing,
        changeCompany,
        isError: isProfileError || isCompaniesError,
        companyId,
        hasCompany,
        companyName,
        companies: companies?.data ?? null,
        role,
        setHasCompany,
        setCompanyName,
        setCompanyId,
        setAuth,
        setMockLoginToken,
        endMockSession,
        isMockLogin,
        mockLoginSessionId,
        mockLoginExpiresAt,
        logout,
        refetchCompanies,
        branchId,
        handleSetBranch,
        refetchProfile,
        featureFlags,
        timesheetActive,
        isFeatureFlagsLoading,
        selectedCompany:
          companies?.data?.find((c) => c.id === companyId) ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
